import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CheckCircle, DownloadSimple, FilePdf, FloppyDisk, Paperclip,
  Plus, Sparkle, Trash, WarningCircle,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { useI18n, useT } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { hasAiAccess } from "../lib/planEntitlements";

const BASIS_KEYS = [
  "device_record",
  "source_document",
  "assessment_record",
  "user_input",
  "ai_suggestion",
  "pending_validation",
  "user_confirmed",
  "not_applicable",
];

function variantLabel(variant) {
  if (typeof variant === "string") return variant.trim();
  if (!variant || typeof variant !== "object") return "";
  return String(variant.name || variant.model || variant.label || variant.variant || "").trim();
}

function downloadBlob(data, filename) {
  const url = URL.createObjectURL(data);
  const anchor = window.document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function UserManualPanel({ device, projectId }) {
  const t = useT();
  const { lang } = useI18n();
  const { user } = useAuth();
  const canGenerateWithAi = hasAiAccess(user);
  const did = device?.id;
  const deviceVariants = useMemo(
    () => [...new Set((device?.variants || []).map(variantLabel).filter(Boolean))],
    [device?.variants]
  );
  const [sources, setSources] = useState([]);
  const [manuals, setManuals] = useState([]);
  const [manual, setManual] = useState(null);
  const [selectedSources, setSelectedSources] = useState([]);
  const [selectedVariants, setSelectedVariants] = useState([]);
  const [additionalContext, setAdditionalContext] = useState("");
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [attestReviewed, setAttestReviewed] = useState(false);
  const [reviewNote, setReviewNote] = useState("");
  const [file, setFile] = useState(null);

  const loadSources = useCallback(async () => {
    if (!did || !projectId) return;
    const { data } = await api.get(
      `/projects/${projectId}/devices/${did}/regulatory-documents`,
      { params: { module: "user_manual" } }
    );
    const next = (data || []).filter((item) => item.record_id === "source");
    setSources(next);
    setSelectedSources((current) => current.filter((id) => next.some((item) => item.id === id)));
  }, [did, projectId]);

  const loadHistory = useCallback(async (selectLatest = false) => {
    if (!did) return;
    const { data } = await api.get(`/devices/${did}/user-manuals`);
    const rows = data || [];
    setManuals(rows);
    if (selectLatest) {
      if (rows.length) {
        const { data: latest } = await api.get(`/devices/${did}/user-manuals/${rows[0].id}`);
        setManual(latest);
        setSelectedSources(latest.source_ids || []);
        setSelectedVariants(latest.variants || []);
        setAdditionalContext(latest.additional_context || "");
      } else {
        setManual(null);
        setSelectedSources([]);
      }
      setDirty(false);
    }
  }, [did]);

  useEffect(() => {
    if (deviceVariants.length) setSelectedVariants(deviceVariants);
    loadSources().catch((e) => setError(formatApiError(e.response?.data?.detail)));
    loadHistory(true).catch((e) => setError(formatApiError(e.response?.data?.detail)));
  }, [deviceVariants, loadHistory, loadSources]);

  async function refreshAfterSave(saved) {
    setManual(saved);
    setSelectedSources(saved.source_ids || []);
    setDirty(false);
    setAttestReviewed(false);
    setNotice("");
    await loadHistory(false);
    return saved;
  }

  async function selectManual(id) {
    if (dirty && !window.confirm(t("manual.discard_changes"))) return;
    setBusy("load");
    setError("");
    try {
      const { data } = await api.get(`/devices/${did}/user-manuals/${id}`);
      setManual(data);
      setSelectedSources(data.source_ids || []);
      setSelectedVariants(data.variants || []);
      setAdditionalContext(data.additional_context || "");
      setDirty(false);
      setAttestReviewed(false);
      setReviewNote("");
      setError("");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  }

  async function uploadSource() {
    if (!file || !projectId) return;
    if (file.size > 5 * 1024 * 1024) {
      setError(t("manual.file_too_large"));
      return;
    }
    setBusy("upload");
    setError("");
    setNotice("");
    try {
      const data = new FormData();
      data.append("file", file);
      data.append("module", "user_manual");
      data.append("record_id", "source");
      await api.post(`/projects/${projectId}/devices/${did}/regulatory-documents`, data);
      setFile(null);
      await loadSources();
      setNotice(t("manual.file_uploaded"));
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  }

  async function deleteSource(source) {
    if (!window.confirm(t("manual.confirm_delete_source"))) return;
    setBusy(source.id);
    setError("");
    try {
      await api.delete(`/projects/${projectId}/devices/${did}/regulatory-documents/${source.id}`);
      setSelectedSources((current) => current.filter((id) => id !== source.id));
      await loadSources();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  }

  async function generate() {
    if (!did) return;
    if (dirty && !window.confirm(t("manual.discard_changes"))) return;
    setBusy("generate");
    setError("");
    setNotice("");
    try {
      const { data } = await api.post(`/devices/${did}/user-manuals/generate`, {
        lang,
        source_ids: selectedSources,
        variants: selectedVariants,
        additional_context: additionalContext,
      });
      setManual(data);
      setDirty(false);
      setSelectedSources(data.source_ids || []);
      setSelectedVariants(data.variants || []);
      setAttestReviewed(false);
      setReviewNote("");
      await loadHistory(false);
      setNotice(t("manual.generated"));
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  }

  async function createTemplate() {
    if (!did) return;
    if (dirty && !window.confirm(t("manual.discard_changes"))) return;
    setBusy("template");
    setError("");
    setNotice("");
    try {
      const { data } = await api.post(`/devices/${did}/user-manuals/template`, {
        lang,
        source_ids: selectedSources,
        variants: selectedVariants,
      });
      setManual(data);
      setDirty(false);
      setSelectedSources(data.source_ids || []);
      setSelectedVariants(data.variants || []);
      setAdditionalContext("");
      setAttestReviewed(false);
      setReviewNote("");
      await loadHistory(false);
      setNotice(t("manual.template_created"));
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  }

  async function persistDraft() {
    if (!manual) return null;
    const { data } = await api.put(`/devices/${did}/user-manuals/${manual.id}`, {
      sections: manual.sections,
      missing_information: manual.missing_information || [],
    });
    return refreshAfterSave(data);
  }

  async function saveDraft() {
    if (!manual) return;
    setBusy("save");
    setError("");
    setNotice("");
    try {
      await persistDraft();
      setNotice(t("manual.saved_new_version"));
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  }

  async function createPdf(downloadNow = false) {
    if (!manual) return null;
    setBusy("pdf");
    setError("");
    setNotice("");
    try {
      const target = dirty ? await persistDraft() : manual;
      const { data } = await api.post(`/devices/${did}/user-manuals/${target.id}/pdf`);
      await refreshAfterSave(data);
      if (downloadNow) await downloadPdf(data.id, data.version);
      setNotice(t("manual.pdf_saved"));
      return data;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
      return null;
    } finally {
      setBusy("");
    }
  }

  async function downloadPdf(manualId, versionOverride) {
    setBusy("download");
    setError("");
    try {
      const { data } = await api.get(`/devices/${did}/user-manuals/${manualId}/pdf`, {
        responseType: "blob",
      });
      const row = manuals.find((item) => item.id === manualId)
        || (manual?.id === manualId ? manual : null);
      const version = versionOverride || row?.version || 1;
      downloadBlob(data, `Manual_Usuario_${(device.name || "dispositivo").replace(/[^\w-]+/g, "_")}_v${version}.pdf`);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  }

  async function markReviewed() {
    if (!manual || !attestReviewed) return;
    setBusy("review");
    setError("");
    setNotice("");
    try {
      let target = dirty ? await persistDraft() : manual;
      if (!target.pdf_name) {
        const { data } = await api.post(`/devices/${did}/user-manuals/${target.id}/pdf`);
        target = data;
        setManual(data);
      }
      const { data } = await api.put(`/devices/${did}/user-manuals/${target.id}/review`, {
        confirmed: true,
        review_note: reviewNote,
      });
      await refreshAfterSave(data);
      setNotice(t("manual.reviewed"));
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  }

  function patchItem(sectionId, itemIndex, key, value) {
    setManual((current) => ({
      ...current,
      sections: current.sections.map((section) => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          items: section.items.map((item, index) => index === itemIndex
            ? { ...item, [key]: value, ...(key === "basis" && value !== "source_document" ? { source_id: "" } : {}) }
            : item),
        };
      }),
    }));
    setDirty(true);
    setAttestReviewed(false);
  }

  function addItem(sectionId) {
    setManual((current) => ({
      ...current,
      sections: current.sections.map((section) => section.id === sectionId
        ? { ...section, items: [...section.items, { text: "", basis: "pending_validation", source_id: "", note: "" }] }
        : section),
    }));
    setDirty(true);
  }

  function removeItem(sectionId, itemIndex) {
    setManual((current) => ({
      ...current,
      sections: current.sections.map((section) => section.id === sectionId
        ? { ...section, items: section.items.filter((_, index) => index !== itemIndex) }
        : section),
    }));
    setDirty(true);
    setAttestReviewed(false);
  }

  function toggleSelection(id, selected, setter) {
    setter((current) => selected ? [...new Set([...current, id])] : current.filter((value) => value !== id));
  }

  const sourceNameById = useMemo(() => {
    const all = [...sources, ...(manual?.sources || [])];
    return Object.fromEntries(all.map((source) => [source.id, source.file_name]));
  }, [sources, manual?.sources]);
  const reviewBlockers = useMemo(() => {
    const items = (manual?.sections || []).flatMap((section) => section.items || []);
    const emptySections = (manual?.sections || []).filter((section) => !(section.items || []).length).length;
    return items.filter((item) => ["pending_validation", "ai_suggestion", "assessment_record", "user_input"].includes(item.basis)).length
      + emptySections
      + (manual?.missing_information || []).length;
  }, [manual]);

  return (
    <section id="user-manual-generator" className="mt-7 border border-blue-200 bg-white shadow-sm" data-testid="user-manual-panel">
      <div className="flex flex-col gap-3 border-b border-blue-100 bg-blue-50/60 p-4 sm:flex-row sm:items-start sm:justify-between sm:p-5">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-2 text-[10px] font-mono font-bold uppercase tracking-widest text-blue-800">
            <FilePdf size={14} /> {t("manual.kicker")}
          </div>
          <h2 className="mt-1 text-lg font-bold text-zinc-900">{t("manual.title")}</h2>
          <p className="mt-1 max-w-3xl text-xs leading-5 text-zinc-600">{t("manual.subtitle")}</p>
        </div>
        {manual && (
          <span className={`inline-flex shrink-0 items-center gap-1 border px-2 py-1 text-[10px] font-bold uppercase tracking-wide ${manual.status === "reviewed" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
            {manual.status === "reviewed" ? <CheckCircle size={12} /> : <WarningCircle size={12} />}
            {manual.status === "reviewed" ? t("manual.status.reviewed") : t("manual.status.draft")}
          </span>
        )}
      </div>

      <div className="space-y-5 p-4 sm:p-5">
        <div className="border-l-4 border-amber-400 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-950">
          <strong>{t("manual.safety_title")}</strong> {t("manual.safety")}
        </div>
        <p className="text-[11px] leading-5 text-zinc-500">{t("manual.ai_privacy")}</p>

        <div className="grid gap-4 lg:grid-cols-2">
          <div className="border border-zinc-200 p-3 sm:p-4">
            <div className="mb-2 flex items-center justify-between gap-2">
              <h3 className="text-xs font-bold uppercase tracking-wide text-zinc-800">{t("manual.sources")}</h3>
              <span className="text-[10px] text-zinc-500">{t("manual.max_source_size")}</span>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                type="file"
                accept=".pdf,application/pdf"
                onChange={(event) => setFile(event.target.files?.[0] || null)}
                className="min-w-0 flex-1 text-xs text-zinc-600 file:mr-2 file:border file:border-zinc-200 file:bg-white file:px-2 file:py-1.5 file:text-xs"
                aria-label={t("manual.choose_pdf")}
              />
              <button type="button" onClick={uploadSource} disabled={!file || busy !== ""}
                className="inline-flex items-center justify-center gap-1 border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-800 disabled:opacity-40">
                <Paperclip size={13} /> {busy === "upload" ? t("manual.uploading") : t("manual.upload")}
              </button>
            </div>
            <div className="mt-1 text-[10px] text-zinc-500">{t("manual.pdf_only")}</div>
            {sources.length ? (
              <div className="mt-3 divide-y divide-zinc-100 border border-zinc-100">
                {sources.map((source) => (
                  <div key={source.id} className="flex items-center gap-2 p-2">
                    <input
                      type="checkbox"
                      checked={selectedSources.includes(source.id)}
                      onChange={(event) => toggleSelection(source.id, event.target.checked, setSelectedSources)}
                      aria-label={`${t("manual.select_source")} ${source.file_name}`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs font-semibold text-zinc-800">{source.file_name}</div>
                      <div className="text-[10px] text-zinc-400">{Math.ceil(source.size / 1024)} KB</div>
                    </div>
                    <button type="button" onClick={() => deleteSource(source)} disabled={busy === source.id}
                      title={t("manual.delete_source")} className="p-1.5 text-red-700 hover:bg-red-50 disabled:opacity-40">
                      <Trash size={14} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-3 border border-dashed border-zinc-200 p-3 text-xs text-zinc-500">{t("manual.no_sources")}</div>
            )}
            {sources.length > 0 && selectedSources.length === 0 && (
              <div className="mt-2 text-[11px] text-amber-700">{t("manual.select_documents_hint")}</div>
            )}
          </div>

          <div className="border border-zinc-200 p-3 sm:p-4">
            <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-zinc-800">{t("manual.scope")}</h3>
            <div className="mb-3 text-xs text-zinc-600">
              {t("manual.device")}: <strong>{device.name || "—"}</strong>
              {device.model ? ` · ${device.model}` : ""}
            </div>
            {deviceVariants.length > 0 && (
              <fieldset>
                <legend className="mb-1 text-[10px] font-bold uppercase tracking-wide text-zinc-500">{t("manual.variants")}</legend>
                <div className="max-h-28 space-y-1 overflow-y-auto">
                  {deviceVariants.map((variant) => (
                    <label key={variant} className="flex items-center gap-2 text-xs text-zinc-700">
                      <input type="checkbox" checked={selectedVariants.includes(variant)}
                        onChange={(event) => toggleSelection(variant, event.target.checked, setSelectedVariants)} />
                      {variant}
                    </label>
                  ))}
                </div>
              </fieldset>
            )}
            <label className="mt-3 block">
              <span className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-500">{t("manual.extra_context")}</span>
              <textarea rows={3} maxLength={10000} value={additionalContext} onChange={(e) => setAdditionalContext(e.target.value)}
                className="w-full border border-zinc-200 px-2 py-1.5 text-xs focus:border-blue-600 focus:outline-none" />
            </label>
            {canGenerateWithAi && (
              <button type="button" onClick={generate} disabled={busy !== ""}
                className="mt-3 inline-flex w-full items-center justify-center gap-2 bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-50"
                data-testid="user-manual-generate">
                <Sparkle size={15} /> {busy === "generate" ? t("manual.generating") : t("manual.generate")}
              </button>
            )}
            <button type="button" onClick={createTemplate} disabled={busy !== ""}
              className="mt-2 inline-flex w-full items-center justify-center gap-2 border border-zinc-300 bg-white px-4 py-2 text-xs font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-50">
              {busy === "template" ? t("manual.creating_template") : t("manual.create_template")}
            </button>
            <p className="mt-1 text-[10px] leading-4 text-zinc-500">{t("manual.template_notice")}</p>
            {canGenerateWithAi && <p className="mt-2 text-[10px] leading-4 text-zinc-500">{t("manual.save_device_first")}</p>}
          </div>
        </div>

        {(error || notice) && (
          <div role={error ? "alert" : "status"} className={`border px-3 py-2 text-xs ${error ? "border-red-200 bg-red-50 text-red-800" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}>
            {error || notice}
          </div>
        )}

        {manuals.length > 0 && (
          <div className="border border-zinc-200 p-3 sm:p-4">
            <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-zinc-800">{t("manual.history")}</h3>
            <div className="flex flex-wrap gap-2">
              {manuals.map((item) => (
                <div key={item.id} className={`flex items-center gap-1 border px-2 py-1.5 text-xs ${manual?.id === item.id ? "border-blue-300 bg-blue-50" : "border-zinc-200 bg-white"}`}>
                  <button type="button" onClick={() => selectManual(item.id)} disabled={busy !== ""}
                    className="text-left font-semibold text-zinc-800 disabled:opacity-50">
                    v{item.version} · {item.status === "reviewed" ? t("manual.status.reviewed") : t("manual.status.draft")}
                  </button>
                  <span className="text-[10px] text-zinc-400">{new Date(item.created_at).toLocaleDateString()}</span>
                  {item.pdf_name && (
                    <button type="button" onClick={() => downloadPdf(item.id)} disabled={busy !== ""}
                      title={t("manual.download_pdf")} className="ml-1 p-1 text-blue-700 hover:bg-blue-100 disabled:opacity-40">
                      <DownloadSimple size={13} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {manual && (
          <div className="space-y-4" data-testid="user-manual-editor">
            <div className="flex flex-col gap-2 border-b border-zinc-200 pb-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-sm font-bold text-zinc-900">{t("manual.editor")} · v{manual.version}</h3>
                <p className="text-[10px] text-zinc-500">{t("manual.created")} {new Date(manual.created_at).toLocaleString()}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={saveDraft} disabled={busy !== "" || !dirty}
                  className="inline-flex items-center gap-1 border border-zinc-300 px-3 py-2 text-xs font-semibold text-zinc-700 disabled:opacity-40">
                  <FloppyDisk size={13} /> {busy === "save" ? t("manual.saving") : t("manual.save_version")}
                </button>
                <button type="button" onClick={() => createPdf(true)} disabled={busy !== ""}
                  className="inline-flex items-center gap-1 border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-800 disabled:opacity-40">
                  <FilePdf size={13} /> {busy === "pdf" || busy === "download" ? t("manual.creating_pdf") : t("manual.save_download")}
                </button>
              </div>
            </div>

            {manual.ai_warnings?.length > 0 && (
              <div className="border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                {manual.ai_warnings.map((warning, index) => <p key={index}>• {warning}</p>)}
              </div>
            )}

            <div className="border border-zinc-200 bg-zinc-50 p-3">
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-600">{t("manual.missing_info")}</label>
              <textarea
                rows={Math.min(6, Math.max(2, (manual.missing_information || []).length))}
                value={(manual.missing_information || []).join("\n")}
                onChange={(event) => {
                  const value = event.target.value.split("\n");
                  setManual((current) => ({ ...current, missing_information: value }));
                  setDirty(true);
                  setAttestReviewed(false);
                }}
                placeholder={t("manual.missing_info_placeholder")}
                className="w-full border border-zinc-200 bg-white px-2 py-1.5 text-xs focus:border-blue-600 focus:outline-none"
              />
            </div>

            <div className="space-y-3">
              {(manual.sections || []).map((section) => (
                <article key={section.id} className="border border-zinc-200">
                  <div className="border-b border-zinc-100 bg-zinc-50 px-3 py-2">
                    <h4 className="text-xs font-bold text-zinc-800">{section.title}</h4>
                  </div>
                  <div className="space-y-3 p-3">
                    {(section.items || []).map((item, index) => (
                      <div key={item.id || `${section.id}-${index}`} className="border border-zinc-100 p-2.5">
                        <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_190px]">
                          <textarea rows={3} value={item.text || ""}
                            onChange={(event) => patchItem(section.id, index, "text", event.target.value)}
                            aria-label={`${section.title} ${t("manual.item_text")}`}
                            className="w-full border border-zinc-200 px-2 py-1.5 text-xs leading-5 focus:border-blue-600 focus:outline-none" />
                          <div className="space-y-2">
                            <label className="block">
                              <span className="mb-1 block text-[9px] font-bold uppercase tracking-wide text-zinc-500">{t("manual.evidence")}</span>
                              <select value={item.basis || "pending_validation"}
                                onChange={(event) => patchItem(section.id, index, "basis", event.target.value)}
                                className="w-full border border-zinc-200 bg-white px-2 py-1.5 text-[11px]">
                                {BASIS_KEYS.map((basis) => <option key={basis} value={basis}>{t(`manual.basis.${basis}`)}</option>)}
                              </select>
                            </label>
                            {item.basis === "source_document" && (
                              <>
                                <label className="block">
                                  <span className="sr-only">{t("manual.source_doc")}</span>
                                  <select value={item.source_id || ""} onChange={(event) => patchItem(section.id, index, "source_id", event.target.value)}
                                    className="w-full border border-zinc-200 bg-white px-2 py-1.5 text-[11px]">
                                    <option value="">{t("manual.select_source")}</option>
                                    {Object.entries(sourceNameById).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                                  </select>
                                </label>
                                <input type="number" min="1" value={item.page || ""}
                                  onChange={(event) => patchItem(section.id, index, "page", event.target.value ? Number(event.target.value) : null)}
                                  placeholder={t("manual.page")}
                                  aria-label={t("manual.page")}
                                  className="w-full border border-zinc-200 px-2 py-1.5 text-[11px]" />
                              </>
                            )}
                            {["user_confirmed", "not_applicable"].includes(item.basis) && (
                              <textarea rows={2} value={item.note || ""}
                                onChange={(event) => patchItem(section.id, index, "note", event.target.value)}
                                placeholder={t("manual.reason_required")}
                                className="w-full border border-zinc-200 px-2 py-1.5 text-[11px]" />
                            )}
                          </div>
                        </div>
                        <div className="mt-1 flex justify-end">
                          <button type="button" onClick={() => removeItem(section.id, index)}
                            className="inline-flex items-center gap-1 px-1.5 py-1 text-[10px] text-red-700 hover:bg-red-50">
                            <Trash size={12} /> {t("manual.remove_item")}
                          </button>
                        </div>
                      </div>
                    ))}
                    <button type="button" onClick={() => addItem(section.id)}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 hover:underline">
                      <Plus size={12} /> {t("manual.add_item")}
                    </button>
                  </div>
                </article>
              ))}
            </div>

            {manual.status !== "reviewed" && (
              <div className="border border-zinc-200 p-3">
                {reviewBlockers > 0 && (
                  <p className="mb-2 text-xs text-amber-800">{t("manual.review_blockers").replace("{count}", String(reviewBlockers))}</p>
                )}
                <label className="flex items-start gap-2 text-xs leading-5 text-zinc-700">
                  <input type="checkbox" checked={attestReviewed} onChange={(event) => setAttestReviewed(event.target.checked)} className="mt-1" />
                  <span>{t("manual.review_attestation")}</span>
                </label>
                <label className="mt-2 block">
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-500">{t("manual.review_note")}</span>
                  <textarea rows={2} value={reviewNote} onChange={(event) => setReviewNote(event.target.value)}
                    className="w-full border border-zinc-200 px-2 py-1.5 text-xs" />
                </label>
                <button type="button" onClick={markReviewed} disabled={!attestReviewed || reviewBlockers > 0 || busy !== ""}
                  className="mt-3 inline-flex items-center gap-1 border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800 disabled:opacity-40">
                  <CheckCircle size={13} /> {busy === "review" ? t("manual.reviewing") : t("manual.mark_reviewed")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
